/**
 * LE SEZIONI DELLA SCHEDA AZIENDA, in un file che non importa niente.
 *
 * Stavano dentro la rotta `/organization/company`. Da lì non si potevano leggere: il percorso
 * guidato ragiona **sulle** sezioni, e importarle dalla rotta avrebbe chiuso un anello — la
 * rotta importa il percorso, il percorso importa la rotta. Un ciclo fra moduli non dà errore
 * in sviluppo: dà un `undefined` a caso all'avvio, in produzione, dove l'ordine di valutazione
 * cambia.
 *
 * Qui dentro non deve entrare altro. È l'elenco, e basta.
 */
export type CompanySection =
  | 'identity' | 'fiscal' | 'international' | 'holding'
  | 'branding' | 'attributes' | 'modules' | 'external-sync' | 'group-policy' | 'rfid-encoding' | 'task-rules' | 'fleet' | 'travel' | 'subscriptions' | 'client-portal'
  | 'project-fields' | 'project-types' | 'working-hour-presets'
  | 'timesheet' | 'orders' | 'leave' | 'planning' | 'schedule-rules'
  | 'attendance' | 'attendance-reminders' | 'tardiness' | 'work-modes' | 'task-board' | 'obligations' | 'circulars';

/** L'ordine in cui compaiono nella barra delle schede. */
export const COMPANY_SECTIONS: readonly CompanySection[] = [
  'identity', 'fiscal', 'international', 'holding',
  'branding', 'attributes', 'modules', 'external-sync', 'group-policy', 'rfid-encoding', 'task-rules', 'fleet', 'travel', 'subscriptions', 'client-portal',
  'project-fields', 'project-types', 'working-hour-presets',
  'timesheet', 'orders', 'leave', 'fleet', 'travel', 'subscriptions', 'client-portal',
  'planning', 'schedule-rules',
  'attendance', 'attendance-reminders', 'tardiness', 'work-modes', 'obligations', 'task-board', 'task-rules',
  'circulars',
];

/**
 * I DUE MESTIERI DELLA SCHEDA, ora che sono due pagine.
 *
 * Fino al 23-09-2026 le diciotto sezioni stavano tutte in `/organization/company`, e la
 * divisione era un **divisore grafico** dentro la barra delle schede («Com'è fatta» / «Come ci
 * si lavora»). Erano due domande diverse e spesso due persone diverse — chi apre «Fiscale»
 * registra un dato, chi apre «Ritardi» stabilisce una politica — quindi ora sono due voci di
 * menu, che è quello che il divisore stava già dicendo.
 *
 * ⚠️ L'obiezione che c'era scritta qui («dividere costringerebbe a scegliere l'azienda due
 * volte») **è decaduta**, e per una ragione precisa: le due pagine lavorano sull'azienda in cui
 * si è entrati, non su un'azienda scelta. Non si sceglie più due volte perché non si sceglie
 * affatto — lo dice la testata.
 *
 * ⚠️ `holding` non sta in nessuna delle due. Quota, tipo di rapporto e capogruppo descrivono un
 * **arco** dell'albero, non l'azienda: appartengono al gruppo, e chi apre la propria scheda li
 * vede comunque in sola lettura («non si dichiara la propria posizione nel gruppo»). Vivono in
 * `/organization/company`, che è la pagina del gruppo. «Holding pura» invece è del nodo — dice
 * cosa fa la società — e resta in `identity`.
 */
export const COMPANY_DATA_SECTIONS: readonly CompanySection[] = [
  'identity', 'fiscal', 'international', 'branding', 'attributes',
];

export const WORK_RULE_SECTIONS: readonly CompanySection[] = [
  /* ⚠️ **L'ordine è quello di `DETAILS`**, che è l'unico che si vede: la barra disegna le schede
     in quell'ordine e questo elenco dice solo **quali** ce ne sono. Tenerli diversi non si nota
     subito — la barra sembra giusta — ma `sectionHome` e il percorso guidato leggono questo, e
     due ordini per la stessa cosa prima o poi dicono due cose. */
  'modules', 'external-sync',
  'project-fields', 'project-types', 'orders',
  'timesheet',
  'working-hour-presets', 'schedule-rules', 'planning',
  'attendance', 'attendance-reminders', 'tardiness',
  'leave', 'work-modes', 'obligations',
  'fleet', 'travel', 'subscriptions', 'client-portal',
  'rfid-encoding',
  'task-board', 'task-rules',
  'circulars',
];
/** Dove vive oggi una sezione: serve ai vecchi indirizzi, che vanno ancora da qualche parte. */
export function sectionHome(s: CompanySection): 'data' | 'rules' | 'group' {
  /* ⚠️ `group-policy` è stata in «Regole di lavoro» per un giorno, il 28-09-2026, e ci stava
     male: quella pagina configura **l'azienda in cui sei**, quella sezione decide cosa possono
     cambiare **le altre**. È il verso opposto, ed è la stessa ragione per cui `holding` non sta
     né di qua né di là — descrive il gruppo, non l'azienda. Vive sulla pagina Holding, sotto
     l'albero delle aziende che governa. */
  if (s === 'holding' || s === 'group-policy') return 'group';
  return COMPANY_DATA_SECTIONS.includes(s) ? 'data' : 'rules';
}

/* ──────────────── le sezioni che non si vedono a tutti ──────────────── */

/**
 * Il claim che serve per **vedere** una sezione. Assente = la vedono tutti.
 *
 * ⚠️ **Un claim e non un interruttore di modulo**, ed è la stessa scelta del menu. Il flag di
 * tenant nasconde solo quando è scritto `false`: un'azienda che il magazzino non l'ha mai
 * comprato non ha nessuna riga, quindi col flag la sezione le comparirebbe lo stesso. Il claim
 * fa l'opposto — si vede dove è stato concesso — ed è il motivo scritto accanto alla voce
 * «Magazzino» in `appNav.ts`.
 *
 * ⚠️ Nascondere non basta: chi arriva con un indirizzo salvato deve **leggere perché** la
 * sezione non c'è, altrimenti conclude che sia sparita. Lo dice il pannello, che la sezione la
 * conosce — qui c'è solo chi la può vedere.
 */
export const SECTION_CLAIM: Partial<Record<CompanySection, string>> = {
  'rfid-encoding': 'WarehouseStock.read',
};

/**
 * CHI ENTRA IN «REGOLE DI LAVORO» — l'amministratore dell'azienda, e chi scrive una sezione.
 *
 * La pagina era dell'amministratore (`ADMIN_CLAIMS`): sono le decisioni che valgono per tutti.
 * Ma alcune sezioni le scrive, sul server, **anche** l'ufficio del personale — lo smart working, i
 * tipi di assenza, le impostazioni dei moduli HR (task 1898, BE v1.247.0) — e l'08-10-2026 si è
 * deciso di farlo entrare lì, invece di dargli una pagina sua: le regole restano organizzate per
 * argomento, ogni cosa ha un posto solo, e ognuno vede **solo** le sezioni che può davvero
 * cambiare.
 *
 * ⚠️ Il permesso è quello della **scrittura** sul server, non un'etichetta di ruolo: chi ha
 * `SmartWorkingPolicy.update` vede «Modalità di lavoro» perché ci può salvare qualcosa. Una sezione
 * fuori da questo elenco resta del solo amministratore.
 *
 * ⚠️ Dentro una sezione possono esserci pezzi che solo l'amministratore scrive — in «Assenze» la
 * scelta di chi approva, che è una colonna dell'azienda (`Tenant.update`). Quei pezzi si mostrano
 * spenti, col motivo: è la sezione intera a decidere se si entra, non il suo campo più esigente.
 */
export const ADMIN_CLAIMS = ['Tenant.update', 'canManageSubTenants'] as const;

export const SECTION_ALSO_FOR: Partial<Record<CompanySection, readonly string[]>> = {
  'work-modes': ['SmartWorkingPolicy.update'],
  'leave': ['LeaveType.update'],
  'attendance': ['TenantAttribute.update'],
  'attendance-reminders': ['TenantAttribute.update'],
  /* Task 1921: l'ufficio del personale scrive anche ritardi, pianificazione e turnazione. */
  'tardiness': ['TardinessPolicy.update'],
  'planning': ['PlanningPolicy.update'],
  'schedule-rules': ['ShiftRotationPattern.update'],
  'obligations': ['ObligationRule.update'],
  /* Task 2055: revisione e canali sono righe di `tenant-settings`, le etichette `circular-labels`. */
  'circulars': ['TenantSetting.update', 'CircularLabel.create'],
};

/** Chi entra nella pagina: l'amministratore, o chi scrive almeno una sezione. */
export const WORK_RULES_ENTRY_CLAIMS: readonly string[] = [
  ...ADMIN_CLAIMS,
  ...new Set(Object.values(SECTION_ALSO_FOR).flat()),
];

/**
 * Il **modulo** da cui una sezione dipende. Assente = la sezione vale sempre.
 *
 * ⚠️ **Una sezione di un modulo spento non si smorza: sparisce.** Il modulo spento è un `403`
 * del server su ogni pagina e ogni chiamata (`MODULE_DISABLED`): configurarne le regole vuol dire
 * compilare una schermata che non governa niente, e la cosa peggiore è che si salva pure — la
 * riga si scrive, e un giorno il modulo si riaccende con dentro decisioni che nessuno ricorda di
 * aver preso.
 *
 * Il menu questo già lo fa (il campo `flag` in `appNav.ts`); le sezioni no, e il 03-10-2026
 * spegnendo «Assenze» la sua scheda restava lì. Qui si chiude l'altra metà.
 *
 * ⚠️ Più moduli nella stessa voce vorrebbe dire **almeno uno**. Oggi non capita: la sezione
 * «Prenotazioni», che serviva al parco auto e alle trasferte insieme, è stata ritirata il
 * 03-10-2026 — con una scheda per modulo le sue tre regole erano orfane, e chi configurava il
 * parco auto le cercava nel parco auto. La forma resta perché il caso tornerà.
 */
/*
 * ⚠️ `string` e non `ModuleFlag`, e non è pigrizia: questo file **non importa niente**, per la
 * ragione scritta in testa. Importare il tipo sarebbe innocuo oggi — un `import type` sparisce in
 * compilazione e un ciclo non lo può fare — ma la regola di questo file è un muro, e un muro con
 * un'eccezione ragionevole dentro è un muro che la prossima eccezione la fa passare.
 *
 * Che i nomi siano flag veri lo controlla `tests/unit/sectionModules.test.ts`, confrontandoli
 * col catalogo: una chiave inventata lì fallisce, ed è la stessa garanzia del tipo.
 */
export const SECTION_MODULES: Partial<Record<CompanySection, readonly string[]>> = {
  'timesheet': ['reporting.enabled'],
  'attendance': ['attendance.enabled'],
  'attendance-reminders': ['attendance.enabled'],
  /* I ritardi si misurano sulle timbrature: senza presenze non c'è il dato su cui si calcolano. */
  'tardiness': ['attendance.enabled'],
  'leave': ['leave.enabled'],
  'work-modes': ['smartWorking.enabled'],
  'fleet': ['fleet.enabled'],
  'travel': ['travel.enabled'],
  'subscriptions': ['subscriptions.enabled'],
  /* ⚠️ «Portale clienti» **non** si lega a un modulo, e non per dimenticanza: il portale un
     `portal.enabled` non ce l'ha — la quota è l'interruttore. Legarlo a un flag che non esiste
     vorrebbe dire nasconderlo sempre, cioè rendere di nuovo impossibile accendere il portale. */
};
